import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Nav, NavItem, NavLink } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import usePermission from '../../Hooks/usePermission';
import { DOCUMENT_VAULT_READ } from '../../constants/permissions';

import Documents from './Documents';

/**
 * Top-level Knowledge Hub page. Mirrors the Compliance / Reporting / Settings
 * pattern: a single sidebar entry opens this landing page, which exposes
 * internal tab navigation. v1 ships with one tab (Document Vault); additional
 * tabs (e.g. Training) will be added as new permissions / features arrive.
 */
const KnowledgeHub = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const allTabs = useMemo(() => [
    {
      key: 'documents',
      label: t('knowledge_hub.documents.title', 'Documents'),
      icon: 'folder',
      path: '/knowledge-hub/documents',
      visible: hasPermission(DOCUMENT_VAULT_READ),
      component: Documents,
      matchPaths: ['/knowledge-hub', '/knowledge-hub/documents'],
    },
  ], [t, hasPermission]);

  const visibleTabs = useMemo(() => allTabs.filter((tab) => tab.visible), [allTabs]);

  const activeTab = useMemo(() => {
    const path = location.pathname;
    const matchedByExtra = visibleTabs.find(
      (tab) => tab.matchPaths && tab.matchPaths.some((p) => path.startsWith(p))
    );
    if (matchedByExtra) return matchedByExtra;
    return visibleTabs.find((tab) => path.startsWith(tab.path));
  }, [location.pathname, visibleTabs]);

  useEffect(() => {
    if (!activeTab && visibleTabs.length > 0) {
      navigate(visibleTabs[0].path, { replace: true });
    }
  }, [activeTab, visibleTabs, navigate]);

  useEffect(() => {
    document.title = `${t('shared.knowledge_hub', 'Knowledge Hub')} | Arcmedix`;
  }, [t]);

  if (!activeTab) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="alert alert-warning mt-4">
            {t('knowledge_hub.no_access', 'You do not have access to any Knowledge Hub modules.')}
          </div>
        </div>
      </div>
    );
  }

  const ActiveComponent = activeTab.component;

  return (
    <>
      <div className="page-content" style={{ paddingBottom: 0 }}>
        <div className="container-fluid">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3"
          >
            <h4 className="mb-0 fw-bold">{t('shared.knowledge_hub', 'Knowledge Hub')}</h4>
          </motion.div>

          <Nav tabs className="knowledge-hub-tabs mb-4 border-bottom">
            {visibleTabs.map((tab) => (
              <NavItem key={tab.key} className="flex-shrink-0">
                <NavLink
                  className={classnames({ active: activeTab.key === tab.key })}
                  onClick={() => navigate(tab.path)}
                  style={{ cursor: 'pointer' }}
                >
                  <FeatherIcon icon={tab.icon} size={16} className="me-2" />
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>
      </div>

      <div className="knowledge-hub-tab-content">
        <ActiveComponent />
      </div>

      <style>{`
        .knowledge-hub-tabs {
          gap: 0.5rem;
          flex-wrap: nowrap;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .knowledge-hub-tabs::-webkit-scrollbar { display: none; }
        .knowledge-hub-tabs .nav-link {
          color: #6c757d;
          background-color: #fff;
          white-space: nowrap;
        }
        .knowledge-hub-tabs .nav-link.active {
          color: #3980c0;
          background-color: #fff;
          border-bottom: 2px solid #3980c0;
        }
        .knowledge-hub-subtabs .nav-link {
          color: #6c757d;
          background-color: #fff;
          white-space: nowrap;
        }
        .knowledge-hub-subtabs .nav-link.active {
          color: #3980c0;
          background-color: #fff;
          border-bottom: 2px solid #3980c0;
        }
        .knowledge-hub-tab-content .page-content {
          padding-top: 0 !important;
        }
      `}</style>
    </>
  );
};

export default KnowledgeHub;
